import { z } from 'zod';

/**
 * Manifiesto de avatares (tanda 3, D-1 a D-4): qué `.glb` corresponde a
 * cada `avatar_id` del backend. El mapeo es del cliente; el backend sólo
 * emite el id y el `generoAparente` del escenario.
 *
 * Vive en `public/avatars/manifest.json` y se lee en runtime. `archivo`
 * es una ruta relativa a `/avatars/` o, como solución temporal del taller,
 * una URL `https://` externa (link firmado de R2, `04_ARQUITECTURA.md`
 * §6.7). Un manifiesto sin assets es válido (D-9): la sesión funciona con
 * el marcador en lugar del avatar.
 *
 * La resolución mira sólo `avatar_id` y `generoAparente`. Nunca el nombre
 * ni el rango etario del avatar: se editan desde el admin.
 */

/** Clave de asset: minúsculas, dígitos y `_` (`marisol`, `f_joven`). */
const ClaveAsset = z.string().regex(/^[a-z0-9_]+$/, 'clave de asset inválida');

/**
 * Ruta relativa a `/avatars/`, sin `/` inicial ni `..`, terminada en
 * `.glb`. Así el manifiesto no puede apuntar fuera de la carpeta que el
 * build verifica.
 */
const RUTA_ARCHIVO = /^[a-z0-9_-]+(\/[a-z0-9_.-]+)*\.glb$/i;

/** Un `esquema:` al comienzo: no es una ruta relativa. */
const CON_ESQUEMA = /^[a-z][a-z0-9+.-]*:/i;

/**
 * Ruta local, o URL `https://` cuya ruta termina en `.glb`. La URL se usa
 * tal cual, con su query string (la firma). `http://` y cualquier otro
 * esquema se rechazan.
 */
export const ArchivoSchema = z.string().superRefine((archivo, ctx) => {
  const problema = CON_ESQUEMA.test(archivo)
    ? problemaDeUrl(archivo)
    : !RUTA_ARCHIVO.test(archivo)
      ? 'ruta de archivo inválida'
      : archivo.split('/').includes('..')
        ? 'la ruta no puede subir de carpeta'
        : null;
  if (problema) ctx.addIssue({ code: z.ZodIssueCode.custom, message: problema });
});

function problemaDeUrl(archivo: string): string | null {
  if (!esUrlExterna(archivo)) return 'una URL externa tiene que ser https://';
  let url: URL;
  try {
    url = new URL(archivo);
  } catch {
    return 'URL inválida';
  }
  return url.pathname.toLowerCase().endsWith('.glb') ? null : 'la URL no apunta a un .glb';
}

/** Si `archivo` es una URL `https://` externa y no una ruta de `/avatars/`. */
export function esUrlExterna(archivo: string): boolean {
  return /^https:\/\//i.test(archivo);
}

/** Con qué URL se pide el asset: la externa tal cual, la ruta bajo `/avatars/`. */
export function urlDeArchivo(archivo: string): string {
  return esUrlExterna(archivo) ? archivo : BASE_AVATARES + archivo;
}

/**
 * La URL sin query string ni fragmento: un link firmado sin la firma, para
 * los avisos de consola.
 */
export function urlSinFirma(url: string): string {
  return url.split(/[?#]/)[0] ?? url;
}

export const ManifiestoAvataresSchema = z
  .object({
    version: z.literal(1),
    assets: z.record(ClaveAsset, z.object({ archivo: ArchivoSchema, genero: z.string() })),
    por_avatar_id: z.record(z.string().min(1), ClaveAsset),
    por_genero: z.record(z.string().min(1), ClaveAsset),
    por_defecto: ClaveAsset.optional(),
  })
  .superRefine((m, ctx) => {
    const existe = (clave: string) => Object.hasOwn(m.assets, clave);
    for (const [id, clave] of Object.entries(m.por_avatar_id)) {
      if (!existe(clave)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['por_avatar_id', id],
          message: `referencia el asset "${clave}", que no está en assets`,
        });
      }
    }
    for (const [genero, clave] of Object.entries(m.por_genero)) {
      if (!existe(clave)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['por_genero', genero],
          message: `referencia el asset "${clave}", que no está en assets`,
        });
      }
    }
    if (m.por_defecto !== undefined && !existe(m.por_defecto)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['por_defecto'],
        message: `referencia el asset "${m.por_defecto}", que no está en assets`,
      });
    }
  });

export type ManifiestoAvatares = z.infer<typeof ManifiestoAvataresSchema>;

/** Manifiesto sin assets: válido, deja la sesión con el marcador (D-9). */
export const MANIFIESTO_VACIO: ManifiestoAvatares = {
  version: 1,
  assets: {},
  por_avatar_id: {},
  por_genero: {},
};

/** Prefijo público de los assets de avatar. */
export const BASE_AVATARES = '/avatars/';

/** Lo que el escenario dice del avatar (`escenario.avatar` de mi-simulacion). */
export interface AvatarDelEscenario {
  id: string;
  generoAparente: string;
}

export interface AssetResuelto {
  clave: string;
  /** `/avatars/<archivo>`, o la URL externa tal cual. */
  url: string;
  /** Qué eslabón de la cadena resolvió (D-3). */
  via: 'avatar_id' | 'genero' | 'defecto';
}

/**
 * Cadena de D-3: `por_avatar_id` → `por_genero[generoAparente]` →
 * `por_defecto`. Cada caída a un eslabón posterior avisa por `avisar`
 * con el `avatar_id`, para que un id sin mapear se note en la consola.
 * Devuelve `null` si nada resuelve: la pantalla muestra el marcador.
 */
export function resolverAsset(
  manifiesto: ManifiestoAvatares,
  avatar: AvatarDelEscenario,
  avisar: (mensaje: string) => void = (m) => console.warn(m),
): AssetResuelto | null {
  const armar = (clave: string, via: AssetResuelto['via']): AssetResuelto | null => {
    const asset = manifiesto.assets[clave];
    return asset ? { clave, url: urlDeArchivo(asset.archivo), via } : null;
  };

  const porId = manifiesto.por_avatar_id[avatar.id];
  if (porId !== undefined) {
    const r = armar(porId, 'avatar_id');
    if (r) return r;
  }

  const genero = avatar.generoAparente.trim().toLowerCase();
  const porGenero = manifiesto.por_genero[genero];
  if (porGenero !== undefined) {
    const r = armar(porGenero, 'genero');
    if (r) {
      avisar(
        `[avatar] avatar_id ${avatar.id} sin asset propio; se usa "${r.clave}" por género "${genero}".`,
      );
      return r;
    }
  }

  if (manifiesto.por_defecto !== undefined) {
    const r = armar(manifiesto.por_defecto, 'defecto');
    if (r) {
      avisar(
        `[avatar] avatar_id ${avatar.id} (género "${genero}") sin asset ni género mapeado; se usa el por defecto "${r.clave}".`,
      );
      return r;
    }
  }

  avisar(`[avatar] avatar_id ${avatar.id} sin asset en el manifiesto; se muestra el marcador.`);
  return null;
}

/**
 * Errores de un manifiesto contra los archivos disponibles. Lo usa el
 * build (D-4): falla si el manifiesto referencia un `.glb` que no existe
 * o uno de desarrollo (`default/`), que no se distribuye. Las URLs
 * externas no se verifican: el build no las ve, y un link firmado vence
 * igual después de publicar.
 *
 * @param existe dice si `archivo` (relativo a `/avatars/`) existe. No se
 * llama con URLs externas.
 */
export function erroresDeArchivos(
  manifiesto: ManifiestoAvatares,
  existe: (archivo: string) => boolean,
): string[] {
  const errores: string[] = [];
  for (const [clave, { archivo }] of Object.entries(manifiesto.assets)) {
    if (esUrlExterna(archivo)) continue;
    if (esDeDesarrollo(archivo)) {
      errores.push(`"${clave}" apunta a ${archivo}: el asset de desarrollo no se distribuye`);
    } else if (!existe(archivo)) {
      errores.push(`"${clave}" apunta a ${archivo}, que no existe`);
    }
  }
  return errores;
}

/** Carpeta del avatar de desarrollo (licencia no distribuible, D-4). */
export const CARPETA_DESARROLLO = 'default';

export function esDeDesarrollo(archivo: string): boolean {
  return archivo.split('/')[0] === CARPETA_DESARROLLO;
}

/** Valida y devuelve el manifiesto, o `null` con el motivo en `avisar`. */
export function parsearManifiesto(
  crudo: unknown,
  origen: string,
  avisar: (mensaje: string) => void = (m) => console.warn(m),
): ManifiestoAvatares | null {
  const r = ManifiestoAvataresSchema.safeParse(crudo);
  if (r.success) return r.data;
  const detalle = r.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('; ');
  avisar(`[avatar] ${origen} inválido — ${detalle}`);
  return null;
}
